import React, { useEffect, useState } from "react";
import {
  Tooltip,
  Image,
  message,
} from "antd";
import {
  getStatusColor,
  makeCamelCase,
  serverImgFullPath,
} from "../utils/Common";
import useUserStore from "../stores/store";
import ConfirmModal from "./modals/ConfirmModal";
import ApiService from "../utils/ApiService";
import ViewCategoryModal from "./modals/ViewCategoryModal";

function CategoryItemComponent({ record, callback, onFinish, onEdit, onFinishCategoryDelete }) {
  const { restaurantId, user, setNetworkCall, showNotification} = useUserStore();
  const [messageApi, contextHolder] = message.useMessage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('Are you sure to delete this category?');
  const [hasDeleteDependency, setHasDeleteDependency] = useState(false);

  const [dependentFoods, setDependentFoods] = useState([]);
  const [dependentSetMenus, setDependentSetMenus] = useState([]);
  const [dependentAddOns, setDependentAddOns] = useState([]);
  const [dependentOrders, setDependentOrders] = useState([]);

  let color = getStatusColor(record.status);
  let dot = { background: color };
  let dotText = { color };
  
  const onClickCancel = () => {
    callback(record);
  };

  const onEditClick = () => {
    onEdit(record);
  };

  const onViewClick = () => {
    setIsViewModalOpen(true)
  };

  const onStaff = () => {
    onFinish("Published", record);
  };

  const offStaff = () => {
    onFinish("Pending", record);
  };

  const deleteCategory = async() => {
    setNetworkCall(true)
    const result = await ApiService.CATEGORY_DELETE(restaurantId, record.id, user.token.token)

    setNetworkCall(false);

    if (result.code !== 200) {
      messageApi.error(result.message);
      return;
    }

    if (result.code === 200 && result.payload.success) {
      // flash success message
      showNotification("Category deleted successfully.", false)
      setIsModalOpen(false);
      onFinishCategoryDelete();
      return;
    }

    const {payload} = result;
    /*Check order exist on this category*/
    if (payload.dependentOrders.length > 0){
      setModalTitle("This category is being used in the following orders. You can delete it after the orders are completed or cancelled.");
      setDependentOrders(payload.dependentOrders);
      setHasDeleteDependency(true);
    } 
    
    if (payload.dependentFoods.length > 0 || payload.dependentSetMenus.length > 0 || payload.dependentAddOns.length > 0){
      setModalTitle("Please, confirm again to delete this category as following related items will also be deleted. Are you sure?");
      setDependentFoods(payload.dependentFoods);
      setDependentSetMenus(payload.dependentSetMenus);
      setDependentAddOns(payload.dependentAddOns);
      setHasDeleteDependency(true);
    }
  }

  const deleteCategoryWithDependencies = async() => {
    try {
      /* If order exist then do not delete and close the modal */
      if (dependentOrders.length > 0) {
        setDependentOrders([]);
        setIsModalOpen(false);
        return;
      }
      const data = {
        categoryId: record.id,
        restaurantId,
        dependentFoods: dependentFoods.map(item => item.id),
        dependentSetMenus: dependentSetMenus.map(item => item.id),
        dependentAddOns: dependentAddOns.map(item => item.id),
      };

      setNetworkCall(true);
      const res = await ApiService.CATEGORY_DELETE_WITH_DEPENDENCIES(data, user.token.token);
      setNetworkCall(false);

      setIsModalOpen(false);

      if (res.code !== 200) {
        messageApi.error(res.message);
      } else {
        onFinishCategoryDelete();
      }
    } catch (error) {
      setNetworkCall(false);
      setIsModalOpen(false);
      console.error("Error deleting category:", error);
      messageApi.error("An error occurred while deleting the category.");
    }finally {
      resetState()
    }
  }

  const onConfirmDelete = () => {
    if (hasDeleteDependency) {
      deleteCategoryWithDependencies();
    } else {
      deleteCategory();
    }
  }

  const handleDeleteIconClick = () => {
    setHasDeleteDependency(false);
    setModalTitle('Are you sure to delete this category?');
    setIsModalOpen(true);
  }

  // Find the restaurant-wise image URL based on the restaurantId
  const restaurantImage = record.restaurantImages?.find(
    (img) => img.restaurantId === restaurantId
  )?.image;

  const closeModal = ()=>{
    resetState()
    setIsModalOpen(false)
  }
  const resetState = ()=>{
    setDependentOrders([])
    setDependentFoods([])
    setDependentSetMenus([])
    setDependentAddOns([])
  }
  return (
    <>
    {contextHolder}
    <div
      key={`category${record.order_id}`}
      className="flex flex-row mb-[16px] font-uregular py-[16px] px-[24px] rounded bg-white items-center"
    >
      <Image
        width={48}
        height={48}
        src={restaurantImage}
        fallback={"/images/tabletap.svg"}
        preview={false}
      />
      <div className="flex flex-col items-start flex-grow justify-center mx-[48px] w-[20px]">
        <div className="text-base text-preblack font-ubold break-words max-w-full">{record.name}</div>
      </div>
      <div className="flex flex-row items-center mx-[4px] xl:w-[110px] 2xl:w-[180px]">
        <div className="text-base text-preblack font-ubold">{record.catTypeText}</div>
      </div>
      <div className="flex flex-row items-center mx-[4px] xl:w-[110px] 2xl:w-[180px]">
        <div className="text-base text-preblack">Variant: {record.variantCount}</div>
      </div>
      {record.status && (
        <div className="flex flex-row items-center mx-[4px] xl:w-[110px] 2xl:w-[180px]">
          <div style={dot} className="w-[12px] h-[12px] rounded-[6px]" />
          <div style={dotText} className="text-md ml-[8px]">
            {makeCamelCase(record.status)}
          </div>
        </div>
      )}

      <div className="flex flex-row items-center px-[8px] w-[52px]">
        {record.status === "pending" && (
          <Tooltip placement="bottom" title="Cancel Category">
            <img
              onClick={onClickCancel}
              className="w-[36px] h-[36px]"
              src={"/images/closeicon.svg"}
            />
          </Tooltip>
        )}
      </div>
      <div className="flex flex-row items-center px-[8px] w-[52px]">
        {record.status !== "Published" ? (
          <Tooltip placement="bottom" title="Off Category">
            <img
              onClick={onStaff}
              className="w-[36px] h-[36px]"
              src={"/images/tickicongrey.svg"}
            />
          </Tooltip>
        ) : (
          <Tooltip placement="bottom" title="On Category">
            <img
              onClick={offStaff}
              className="w-[36px] h-[36px]"
              src={"/images/tickicon.svg"}
            />
          </Tooltip>
        )}
      </div>

      <Tooltip placement="bottom" title="View Category">
        <img
          onClick={() => onViewClick()}
          className="w-[36px] h-[36px] mr-[6px] cursor-pointer"
          src={"/images/viewicon.svg"}
        />
      </Tooltip>

      <Tooltip placement="bottom" title="Edit Category">
        <img
          onClick={() => onEditClick(record)}
          className="w-[30px] h-[30px] ml-[-3px] cursor-pointer"
          src={"https://tabletap-cdn.s3.ap-south-1.amazonaws.com/tabletap-prod/static-assets/edit_user_icon.svg"}
        />
      </Tooltip>
      <Tooltip placement="bottom" title="Delete Category">
        <img
          onClick={handleDeleteIconClick}
          className="w-[30px] h-[30px] ml-[6px] cursor-pointer"
          src={"/images/deleteicon.svg"}
        />
      </Tooltip>

      <ConfirmModal 
        isModalOpen={isModalOpen}
        records={{
          message: modalTitle,
          handleOk: onConfirmDelete,
          handleCancel: () => closeModal(),
          confirmText: dependentOrders.length > 0 ? "Ok" : 'Yes',
          cancelconfirmText: "No"
        }}
        showCancel = {dependentOrders.length > 0 ? false : true}
        extraContent={<div className="mb-10 mt-5">
          {/*Order List*/}
          {dependentOrders.length > 0 && (<div className="mb-2">
            <div className="text-base text-preblack font-ubold mb-3">Order List:</div>
            {dependentOrders.map(order => (
              <div key={order.id} className="border p-2 mb-2">{order.orderNumber}</div>
            ))}
          </div>)}

          {/*Single Food List*/}
          {dependentFoods.length > 0 && (<div className="mb-2">
            <div className="text-base text-preblack font-ubold mb-3">Single Food List:</div>
            {dependentFoods.map(order => (
              <div key={order.id} className="border p-2 mb-2">{order.name}</div>
            ))}
          </div>)}

          {/*Combo Food List*/}
          {dependentSetMenus.length > 0 && (<div className="mb-2">
            <div className="text-base text-preblack font-ubold mb-3">Combo Food List:</div>
            {dependentSetMenus.map(order => (
              <div key={order.id} className="border p-2 mb-2">{order.name}</div>
            ))}
          </div>)}

          {/*AddOns List*/}
          {dependentAddOns.length > 0 && (<div className="mb-2">
            <div className="text-base text-preblack font-ubold mb-3">AddOns List:</div>
            {dependentAddOns.map(order => (
              <div key={order.id} className="border p-2 mb-2">{order.name}</div>
            ))}
          </div>)}

      </div>}
      />

      {/*View Category details*/}
      {isViewModalOpen && (
        <ViewCategoryModal isViewModalOpen={isViewModalOpen} setIsViewModalOpen={setIsViewModalOpen} record={record} />
      )}
    </div>
    </>
  );
}

export default CategoryItemComponent;
